import "@/index.css";
import "@/App.css";
import { HashRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { Toaster } from "./components/ui/sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ClientDashboard from "./pages/ClientDashboard";
import CreateTicketPage from "./pages/CreateTicketPage";
import TicketDetailPage from "./pages/TicketDetailPage";
import OperatorDashboard from "./pages/OperatorDashboard";
import StaffManagementPage from "./pages/StaffManagementPage";
import CategoriesPage from "./pages/CategoriesPage";
import UsersManagementPage from "./pages/UsersManagementPage";
import UserSettingsPage from "./pages/UserSettingsPage";
import AdminSettingsPage from "./pages/AdminSettingsPage";
import GuestTicketPage from "./pages/GuestTicketPage";
import TicketSuccessPage from "./pages/TicketSuccessPage";
import LandingPage from "./pages/LandingPage";

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    if (requiredRole === 'operator' && user.ruolo === 'cliente') {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

// Auth Route Component (redirects if already logged in)
const AuthRoute = ({ children }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
            </div>
        );
    }

    if (user) {
        if (user.ruolo === 'cliente') {
            return <Navigate to="/dashboard" replace />;
        } else {
            return <Navigate to="/operator" replace />;
        }
    }

    return children;
};

// Home Redirect Component
const HomeRedirect = () => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
            </div>
        );
    }

    // If logged in, redirect to appropriate dashboard
    if (user) {
        if (user.ruolo === 'cliente') {
            return <Navigate to="/dashboard" replace />;
        } else {
            return <Navigate to="/operator" replace />;
        }
    }

    // If not logged in, show landing page
    return <LandingPage />;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Home redirect */}
            <Route path="/" element={<HomeRedirect />} />
            
            {/* Public guest ticket route */}
            <Route path="/richiedi-assistenza" element={<GuestTicketPage />} />
            <Route path="/ticket-inviato" element={<TicketSuccessPage />} />
            
            {/* Auth routes */}
            <Route 
                path="/login" 
                element={
                    <AuthRoute>
                        <LoginPage />
                    </AuthRoute>
                } 
            />
            <Route 
                path="/register" 
                element={
                    <AuthRoute>
                        <RegisterPage />
                    </AuthRoute>
                } 
            />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Client routes */}
            <Route 
                path="/dashboard" 
                element={
                    <ProtectedRoute>
                        <ClientDashboard />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/tickets/new" 
                element={
                    <ProtectedRoute>
                        <CreateTicketPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/tickets/:id" 
                element={
                    <ProtectedRoute>
                        <TicketDetailPage />
                    </ProtectedRoute>
                } 
            />

            {/* Operator routes */}
            <Route 
                path="/operator" 
                element={
                    <ProtectedRoute requiredRole="operator">
                        <OperatorDashboard />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/staff" 
                element={
                    <ProtectedRoute requiredRole="operator">
                        <StaffManagementPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/categories" 
                element={
                    <ProtectedRoute requiredRole="operator">
                        <CategoriesPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/users" 
                element={
                    <ProtectedRoute requiredRole="operator">
                        <UsersManagementPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/settings" 
                element={
                    <ProtectedRoute>
                        <UserSettingsPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/admin/settings" 
                element={
                    <ProtectedRoute requiredRole="operator">
                        <AdminSettingsPage />
                    </ProtectedRoute>
                } 
            />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <AuthProvider>
            <HashRouter>
                <AppRoutes />
                <Toaster richColors position="top-right" />
            </HashRouter>
        </AuthProvider>
    );
}

export default App;
